// src/features/wf3/popup/W3Porcentagem.jsx
import React from "react";

/**
 * Aderência do WF-3
 *
 * Props:
 * - tempoPrevistoMinutos: número (minutos planejados do intervalo)
 * - tempoRealizadoMinutos: número (minutos realizados)
 * - quantidadePrevista: número (quantidade planejada)
 * - quantidadeRealizada: número (quantidade realizada)
 */
export default function W3Porcentagem({
  tempoPrevistoMinutos = null,
  tempoRealizadoMinutos = null,
  quantidadePrevista = null,
  quantidadeRealizada = null,
}) {
  const LIMIAR = 0.95;

  const safeRatio = (real, previsto) => {
    if (real == null || previsto == null) return null;
    const num = Number(real);
    const den = Number(previsto);
    if (!isFinite(num) || !isFinite(den) || den <= 0) return null;
    return num / den; // 1 = 100%, 0.8 = 80%, 1.2 = 120%
  };

  const ratioTempo = safeRatio(tempoRealizadoMinutos, tempoPrevistoMinutos);
  const ratioQtd = safeRatio(quantidadeRealizada, quantidadePrevista);

  const pctFromRatio = (r) => (r == null ? null : Math.max(0, r * 100));

  const pctTempo = pctFromRatio(ratioTempo);
  const pctQtd = pctFromRatio(ratioQtd);

  const tempoOk = ratioTempo == null ? true : ratioTempo >= LIMIAR;
  const qtdOk = ratioQtd == null ? true : ratioQtd >= LIMIAR;

  const hasTempo = pctTempo != null && isFinite(pctTempo);
  const hasQtd = pctQtd != null && isFinite(pctQtd);

  // ===== Texto / cores do status geral =====
  let statusText = "Sem dados de aderência";
  let statusColor = "#6b7280"; // cinza
  let statusBg = "#f3f4f6";

  if (!hasTempo && !hasQtd) {
    statusText = "Sem dados de aderência";
    statusColor = "#6b7280";
    statusBg = "#f3f4f6";
  } else if (tempoOk && qtdOk) {
    statusText = "ADERENTE";
    statusColor = "#16a34a"; // verde
    statusBg = "#dcfce7";
  } else if (!tempoOk && qtdOk) {
    statusText = "Tempo de intervalo não aderente";
    statusColor = "#b91c1c"; // vermelho
    statusBg = "#fee2e2";
  } else if (tempoOk && !qtdOk) {
    statusText = "Produção não aderente";
    statusColor = "#b91c1c";
    statusBg = "#fee2e2";
  } else {
    statusText = "Intervalo e produção não aderentes";
    statusColor = "#b91c1c";
    statusBg = "#fee2e2";
  }

  const pctTempoText = !hasTempo ? "—" : `${Math.round(pctTempo)}%`;
  const pctQtdText = !hasQtd ? "—" : `${Math.round(pctQtd)}%`;

  // Estilos base (mais compactos)
  const rowLabelStyle = {
    font: '600 10px/1 "Inter", system-ui, sans-serif',
    color: "#0f3b2e",
  };

  const pctSmallStyle = (ok, hasData) => {
    let color = "#0B4D3B";
    if (!hasData) color = "#9ca3af";
    else if (!ok) color = "#b91c1c";
    return {
      font: '700 10px/1 "Inter", system-ui, sans-serif',
      color,
      whiteSpace: "nowrap",
      marginLeft: 4,
    };
  };

  const barBgStyle = {
    width: "100%",
    height: 5,
    borderRadius: 999,
    background: "#e5e7eb",
    overflow: "hidden",
  };

  const barFillStyle = (pct, ok) => {
    const pctClamped =
      pct == null || !isFinite(pct)
        ? 0
        : Math.max(0, Math.min(pct, 150)); // trava visual 150%
    return {
      width: `${pctClamped}%`,
      height: "100%",
      background: ok ? "#0B4D3B" : "#b91c1c",
      transition: "width 0.25s ease-out",
    };
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: 4,
        borderRadius: 8,
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        minWidth: 150,
      }}
    >
      {/* Status geral centralizado em cima */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <span
          style={{
            padding: "2px 8px",
            borderRadius: 999,
            background: statusBg,
            color: statusColor,
            font: '800 10px/1 "Inter", system-ui, sans-serif',
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          {statusText}
        </span>
      </div>

      {/* Intervalo e Produção lado a lado */}
      <div
        style={{
          display: "flex",
          gap: 6,
          alignItems: "stretch",
        }}
      >
        {/* Intervalo */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span style={rowLabelStyle}>Intervalo</span>
            <span style={pctSmallStyle(tempoOk, hasTempo)}>
              {pctTempoText}
            </span>
          </div>
          <div style={barBgStyle}>
            <div style={barFillStyle(pctTempo, tempoOk)} />
          </div>
        </div>

        {/* Produção */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span style={rowLabelStyle}>Produção</span>
            <span style={pctSmallStyle(qtdOk, hasQtd)}>{pctQtdText}</span>
          </div>
          <div style={barBgStyle}>
            <div style={barFillStyle(pctQtd, qtdOk)} />
          </div>
        </div>
      </div>
    </div>
  );
}
